import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@treezor/design-system';

import { SummaryInformation } from '@/ui-kit/SummaryInformation/SummaryInformation';

import { CompanyMembersSummary } from '@/features/user/components/CreateUser/parts/business/companyMembers/CompanyMembersSummary';
import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { SanctionEmbargoSummary } from '@/features/user/components/CreateUser/SanctionEmbargoSummary';
import { TaxResidenceSummary } from '@/features/user/components/CreateUser/TaxResidenceSummary';
import { KycDocumentSummary } from '@/features/user/components/CreateUser/KycDocumentSummary';
import { useMembers } from '@/features/user/hooks/createUser/useMembers';
import { legalForms } from '@/features/user/store/user.model';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

export function CreateUserBusinessSummary({ stepsLength }: ICreateUserPartProps) {
  const { t } = useTranslation();

  const { distributionCountry, user, isLoading, userId, onConfirmCreation, navigateDashboard } =
    useCreateBusinessUser();

  const { isTaxResidenceLoading, isTaxResidenceCreated } = useAddTaxResidences(userId);
  const { isKycDocumentLoading, isKycDocumentsUploaded } = useCreateUserKycDocuments(userId);

  const {
    isMemberCreated,
    isMemberLoading,
    isMembersTaxResidencesCreated,
    isMembersDocumentsUploaded,
    isMembersTaxResidencesLoading,
    isMembersDocumentsLoading,
  } = useMembers(userId);

  useEffect(() => {
    if (
      userId &&
      isTaxResidenceCreated &&
      isKycDocumentsUploaded &&
      isMemberCreated &&
      isMembersTaxResidencesCreated &&
      isMembersDocumentsUploaded
    ) {
      navigateDashboard(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    isTaxResidenceCreated,
    isKycDocumentsUploaded,
    isMemberCreated,
    isMembersDocumentsUploaded,
    isMembersDocumentsUploaded,
  ]);

  const hasAddress: boolean = !!(user.address1 || user.address2 || user.postcode || user.city || user.country);

  return (
    <div className="flex flex-col gap-6">
      <SummaryInformation
        title={t('pages.dashboard.createUser.form.pages.businessInformation')}
        sections={[
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.mainInformation'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.email'), value: user?.email },
                  distributionCountry?.length > 0 && {
                    label: t('pages.currentProfiles.profile.informations.distributionCountry'),
                    value: t(`shared.countries.${user.distributionCountry}`),
                  },
                ],
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber'),
                    value: user?.legalRegistrationNumber,
                  },
                  { label: t('pages.dashboard.createUser.form.legalName'), value: user?.legalName || '-' },
                  {
                    label: t('pages.dashboard.createUser.form.legalRegistrationDate'),
                    value: user?.legalRegistrationDate || '-',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalForm'),
                    value: user?.legalForm ? `${user.legalForm} - ${legalForms[user.legalForm]}` : '-',
                  },
                  { label: t('pages.dashboard.createUser.form.legalTvaNumber'), value: user?.legalTvaNumber || '-' },
                  {
                    label: t('pages.dashboard.createUser.form.legalShareCapital'),
                    value: user?.legalShareCapital ? `${user?.legalShareCapital}` : '-',
                  },
                ],
              },
              {
                component: (
                  <>
                    <div className="flex flex-col gap-2 typo-body-regular-paragraph font-bold">
                      <p className="text-tertiary">{t('pages.currentProfiles.profile.contact.address')}</p>
                      {hasAddress ? (
                        <div>
                          <p>{user.address1 || t('pages.dashboard.createUser.form.noAddress')}</p>
                          <p>{user.address2}</p>
                          <p>{user.address3}</p>
                          <p>
                            {user.postcode || t('pages.dashboard.createUser.form.noPostcode')}{' '}
                            {user.city || t('pages.dashboard.createUser.form.noCity')}
                          </p>
                          <p>
                            {user.country
                              ? t(`shared.countries.${user.country}`)
                              : t('pages.dashboard.createUser.form.noCountry')}
                          </p>
                        </div>
                      ) : (
                        '-'
                      )}
                    </div>
                  </>
                ),
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.legalSector'),
                    value: `${user?.legalSectorType} - ${user?.legalSector}`,
                  },
                ],
              },
            ],
          },
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.contact'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.phone'), value: user?.phone || '-' },
                  { label: t('pages.dashboard.createUser.form.mobile'), value: user?.mobile || '-' },
                ],
              },
            ],
          },
        ]}
        key={`summary-businessInformation`}
      />
      <SummaryInformation
        title={t('pages.dashboard.createUser.form.pages.verificationDetails')}
        sections={[
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.situation'),
            categories: [
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.legalAnnualTurnOver'),
                    value: user?.legalAnnualTurnOver
                      ? t(`pages.dashboard.createUser.form.legalAnnualTurnOvers.${user.legalAnnualTurnOver}`)
                      : '-',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalNetIncomeRanges.title'),
                    value: user?.legalNetIncomeRange
                      ? t(`pages.dashboard.createUser.form.legalNetIncomeRanges.${user.legalNetIncomeRange}`)
                      : '-',
                  },
                  {
                    label: t('pages.currentProfiles.profile.financial.isOnStockExchange'),
                    value: user?.isOnStockExchange
                      ? t(`translation:components.molecules.forms.${user.isOnStockExchange ? 'yes' : 'no'}`)
                      : '-',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.sourceOfFunds.title'),
                    value: user?.sourceOfFunds
                      ? t(`pages.dashboard.createUser.form.sourceOfFunds.${user?.sourceOfFunds}`)
                      : '-',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalNumberOfEmployee'),
                    value: user?.legalNumberOfEmployeeRange || '-',
                  },
                ],
              },
              {
                component: (
                  <SanctionEmbargoSummary
                    entitySanctionsQuestionnaire={user?.entitySanctionsQuestionnaire}
                    activityOutsideEu={user?.activityOutsideEu}
                    economicSanctions={user?.economicSanctions}
                    residentCountriesSanctions={user?.residentCountriesSanctions}
                    involvedSanctions={user?.involvedSanctions}
                  />
                ),
              },
            ],
          },
          {
            subTitle: t('pages.currentProfiles.profile.taxResidence.cardTitle'),
            categories: [
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.usTaxResident'),
                    value:
                      Number(user?.specifiedUSPerson || 0) === 1
                        ? t('translation:components.molecules.forms.yes')
                        : t('translation:components.molecules.forms.no'),
                  },
                ],
              },
              {
                component: <TaxResidenceSummary taxResidences={user?.taxResidences} />,
              },
            ],
          },
          ...(user?.kycDocuments?.length > 0
            ? [
                {
                  subTitle: t('pages.dashboard.createUser.form.blocks.documents'),
                  categories: [
                    {
                      component: <KycDocumentSummary documents={user.kycDocuments} />,
                    },
                  ],
                },
              ]
            : []),
        ]}
        key={`summary-verificationDetails`}
      />

      <SummaryInformation
        title={t('pages.dashboard.createUser.form.pages.companyMembers')}
        sections={[
          {
            component: user?.members?.length ? (
              <CompanyMembersSummary />
            ) : (
              <p className="typo-body-regular-paragraph text-secondary font-bold">
                {t('pages.dashboard.createUser.business.steps.members.noMembers.title')}
              </p>
            ),
          },
        ]}
        key={`summary-companyMembers`}
      />

      <Divider />
      <CreateUserFooter
        onNext={onConfirmCreation}
        nextTitle={t('pages.dashboard.createUser.form.actions.confirmCreation')}
        isLoading={
          isLoading ||
          isMemberLoading ||
          isTaxResidenceLoading ||
          isKycDocumentLoading ||
          isMembersTaxResidencesLoading ||
          isMembersDocumentsLoading
        }
        stepsLength={stepsLength}
      />
    </div>
  );
}
